/**
 * 再生位置のローカルミラー（localStorage）
 *
 * iOS でスリープ中に再生を続けると、JS タイマーやネットワークが抑制されて
 * サーバーへの進捗保存が届かないまま PWA のプロセスが kill されることがある。
 * localStorage は同期書き込みなのでプロセス kill の直前でも確実に残り、
 * 次回起動時にサーバー値と突き合わせて新しい方を採用できる。
 */
const KEY_PREFIX = 'playback-position:'

export type CachedPlaybackPosition = {
  /** 再生位置（秒） */
  position: number
  /** 保存時刻（epoch ms） */
  updatedAt: number
}

const buildKey = (episodeId: number): string => `${KEY_PREFIX}${episodeId}`

export const readCachedPosition = (episodeId: number): CachedPlaybackPosition | null => {
  const raw = localStorage.getItem(buildKey(episodeId))
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { position, updatedAt } = parsed as Partial<CachedPlaybackPosition>
    if (!Number.isFinite(position) || !Number.isFinite(updatedAt)) return null
    return { position: position as number, updatedAt: updatedAt as number }
  } catch {
    return null
  }
}

export const writeCachedPosition = (episodeId: number, position: number): void => {
  if (!Number.isFinite(position) || position < 0) return
  const value: CachedPlaybackPosition = { position: Math.floor(position), updatedAt: Date.now() }
  try {
    localStorage.setItem(buildKey(episodeId), JSON.stringify(value))
  } catch {
    // 容量超過・プライベートモード等は無視（サーバー保存が本流）
  }
}

export const clearCachedPosition = (episodeId: number): void => {
  localStorage.removeItem(buildKey(episodeId))
}
